// FirewallSidebar.tsx
import { useSearchParams } from "react-router-dom";
import { useMemo, useEffect } from "react";
import { ShieldAlert, LayoutDashboard, Network, Hash, Shield, BookMarked, Globe, FileText, ShieldCheck, Activity, AlertTriangle, Logs, Users, Wifi, Terminal } from "lucide-react";
import { hasPermission } from "../../../../utils/permissionsHelper";

export interface FirewallTab {
    id: string;
    label: string;
    icon: React.ReactNode;
    permissionKey?: string;
}

export const FIREWALL_TABS: FirewallTab[] = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard size={16} /> },
    { id: "ssh", label: "SSH", icon: <Terminal size={16} /> },
    { id: "firewall-logs", label: "Firewall Logs", icon: <Logs size={16} /> },
    { id: "interface", label: "Interfaces", icon: <Network size={16} /> },
    { id: "rules", label: "Firewall Rules", icon: <Hash size={16} /> },
    { id: "vpn", label: "VPN", icon: <Shield size={16} /> },
    { id: "objects", label: "Objects & Groups", icon: <BookMarked size={16} /> },
    { id: "network", label: "Network", icon: <Globe size={16} /> },
    { id: "certificate", label: "Certificates", icon: <FileText size={16} /> },
    { id: "compliance", label: "Compliance", icon: <ShieldCheck size={16} /> },
    { id: "traffic-analytics", label: "Traffic & Dos", icon: <Activity size={16} /> },
    { id: "security-profiles", label: "Security Profiles", icon: <AlertTriangle size={16} /> },
    // { id: "hardware", label: "Hardware", icon: <HardDrive size={16} /> },
    { id: "logs", label: "Logs", icon: <Logs size={16} /> },
    { id: "users", label: "Users", icon: <Users size={16} /> },
    { id: "web-filters", label: "Web Filters", icon: <Wifi size={16} /> },
    { id: "license", label: "Licenses & Services", icon: <FileText size={16} /> },
];

export function FirewallSidebar({
    tabs,
    activeTab,
    onTabChange,
}: {
    tabs: FirewallTab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
}) {
    return (
        <div className="bg-gray-900 border-r border-gray-800 flex flex-col h-full w-52 shrink-0">
            {/* Header */}
            <div className="h-12 w-full shrink-0 border-b border-gray-800 flex items-center px-3 gap-3">
                <div className="bg-orange-600/20 p-1.5 rounded-lg border border-orange-500/30 shrink-0">
                    <ShieldAlert className="text-orange-400 h-4 w-4" />
                </div>
                <h1 className="text-sm font-bold text-white tracking-wide whitespace-nowrap">
                    Firewall
                </h1>
            </div>

            {/* Tab List */}
            <div className="flex-1 overflow-y-auto custom-scroll p-1.5 space-y-1">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => onTabChange(tab.id)}
                        className={`w-full flex items-center gap-3 px-3 h-9 rounded-md transition-all duration-200 border ${activeTab === tab.id
                            ? "bg-orange-600 text-white shadow-sm border-orange-500/20"
                            : "text-gray-200 hover:text-gray-200 hover:bg-gray-800 border-transparent"
                            }`}
                    >
                        <span className="shrink-0 flex items-center justify-center w-5 h-5">
                            {tab.icon}
                        </span>
                        <span className="font-bold text-[11px] uppercase tracking-wider whitespace-nowrap">
                            {tab.label}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── Permission-aware hooks ───────────────────────────────────────────────────
export function useFirewallPermittedTabs() {
    return useMemo(() => {
        return FIREWALL_TABS.filter((tab) => {
            if (tab.permissionKey) {
                return hasPermission(tab.permissionKey, "read");
            }
            return true;
        });
    }, []);
}

export function useFirewallActiveTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const permittedTabs = useFirewallPermittedTabs();

    const requestedTab = searchParams.get("tab");

    const activeTab = useMemo(() => {
        const isValidTab = permittedTabs.some((tab) => tab.id === requestedTab);
        return isValidTab && requestedTab ? requestedTab : (permittedTabs[0]?.id || "");
    }, [requestedTab, permittedTabs]);

    useEffect(() => {
        if (permittedTabs.length > 0 && requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, permittedTabs, setSearchParams]);

    return {
        permittedTabs,
        activeTab,
        setTab: (id: string) => setSearchParams({ tab: id }),
    };
}
